import { CenterSpin } from '@/components/CenterSpin';
import { DetailsPageLayout } from '@/layouts/DetailsPageLayout';
import { downloadFile, useDeleteFile, useFile } from '@/queries/files';
import { formatBytes, formatRelativeTime, getErrorMessage } from '@/utils';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import ShareAltOutlined from '@ant-design/icons/ShareAltOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { styled, useTheme } from 'styled-components';
import { DetailRow } from 'ui-components';

const { Text } = Typography;

const StatusBanner = styled.div<{
  $status: 'pending' | 'completed' | 'failed';
}>`
  ${({ theme, $status }) => `
  display: flex;
  align-items: center;
  gap: ${theme.marginXS}px;
  padding: ${theme.paddingXS}px ${theme.paddingSM}px;
  border-radius: ${theme.borderRadius}px;
  margin-bottom: ${theme.margin}px;
  
  ${
    $status === 'failed'
      ? `
        background: ${theme.colorErrorBg};
        border: ${theme.lineWidth}px solid ${theme.colorErrorBorder};
        color: ${theme.colorErrorText};
      `
      : $status === 'pending'
        ? `
        background: ${theme.colorWarningBg};
        border: ${theme.lineWidth}px solid ${theme.colorWarningBorder};
        color: ${theme.colorWarningText};
      `
        : `
        background: ${theme.colorSuccessBg};
        border: ${theme.lineWidth}px solid ${theme.colorSuccessBorder};
        color: ${theme.colorSuccessText};
      `
  }
`}
`;

const SectionContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.marginLG}px;
`;

const UppercaseLabel = styled(Typography.Text)`
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

export const FileDetailPage: React.FC = () => {
  const { teamId, fileId } = useParams<{
    teamId: string;
    fileId: string;
  }>();
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const theme = useTheme();

  const {
    data: file,
    isLoading,
    error,
  } = useFile(fileId || '', {
    enabled: !!fileId,
  });

  const deleteFileMutation = useDeleteFile();

  // Loading states
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadFile = async () => {
    if (!file) return;

    setIsDownloading(true);
    try {
      await downloadFile(file.fileId, file.fileName);
      void message.success('Download started');
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to download file');
      void message.error(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShareFile = async () => {
    try {
      const currentUrl = window.location.href;
      await navigator.clipboard.writeText(currentUrl);
      void message.success('File URL copied to clipboard');
    } catch (error) {
      void message.error('Failed to copy URL to clipboard');
    }
  };

  const handleCopyId = async () => {
    if (!file) return;

    try {
      await navigator.clipboard.writeText(file.fileId);
      void message.success('File ID copied to clipboard');
    } catch (error) {
      void message.error('Failed to copy ID');
    }
  };

  const handleDeleteFile = () => {
    if (!file) return;

    void modal.confirm({
      title: 'Delete File',
      content: (
        <div>
          <p>
            Are you sure you want to delete the file{' '}
            <strong>"{file.fileName}"</strong>?
          </p>
          <Text type="danger">
            <strong>Warning:</strong> This action cannot be undone. The file
            will be permanently deleted from storage.
          </Text>
        </div>
      ),
      okText: 'Delete File',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteFileMutation.mutateAsync(file.fileId);
          void message.success(
            `File "${file.fileName}" has been deleted successfully`,
          );
          void navigate(`/team/${teamId}/files`);
        } catch (error) {
          const errorMessage = getErrorMessage(error, 'Failed to delete file');
          void message.error(errorMessage);
        }
      },
    });
  };

  const getUploadStatusTag = (status: string) => {
    switch (status) {
      case 'completed':
        return <Tag color="success">Ready</Tag>;
      case 'pending':
        return <Tag color="processing">Uploading</Tag>;
      case 'failed':
        return <Tag color="error">Failed</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  // Determine layout props based on state
  const getLayoutProps = () => {
    if (!teamId || !fileId) {
      return {
        title: 'Missing Information',
        breadcrumbs: [
          {
            label: 'Files',
            path: teamId ? `/team/${teamId}/files` : '/',
          },
          { label: 'Error' },
        ],
      };
    }

    if (isLoading) {
      return {
        title: 'Loading File',
        breadcrumbs: [
          { label: 'Files', path: `/team/${teamId}/files` },
          { label: 'Loading...' },
        ],
      };
    }

    if (error) {
      return {
        title: 'Error Loading File',
        breadcrumbs: [
          { label: 'Files', path: `/team/${teamId}/files` },
          { label: 'Error' },
        ],
      };
    }

    if (!file) {
      return {
        title: 'File Not Found',
        breadcrumbs: [
          { label: 'Files', path: `/team/${teamId}/files` },
          { label: 'Not Found' },
        ],
      };
    }

    return {
      title: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: `${theme.margin}px`,
          }}
        >
          {file.fileName}
          {getUploadStatusTag(file.uploadStatus)}
        </div>
      ),
      breadcrumbs: [
        { label: 'Files', path: `/team/${teamId}/files` },
        { label: file.fileName },
      ],
      actions: [
        {
          label: 'Download',
          type: 'primary' as const,
          icon: <DownloadOutlined />,
          onClick: handleDownloadFile,
          loading: isDownloading,
          disabled: file.uploadStatus !== 'completed',
        },
        {
          label: 'Share file',
          type: 'primary' as const,
          icon: <ShareAltOutlined />,
          onClick: handleShareFile,
        },
        {
          label: 'Copy ID',
          type: 'primary' as const,
          icon: <CopyOutlined />,
          onClick: handleCopyId,
        },
        {
          label: 'Delete file',
          type: 'primary' as const,
          icon: <DeleteOutlined />,
          danger: true,
          divided: true,
          onClick: handleDeleteFile,
        },
      ],
    };
  };

  const layoutProps = getLayoutProps();

  // Render different content based on state
  const renderContent = () => {
    if (!teamId || !fileId) {
      return (
        <DetailsPageLayout.TopContent>
          <Card>
            <Typography.Text type="danger">
              Unable to load file details. Team or file information is missing.
            </Typography.Text>
            <div style={{ marginTop: `${theme.margin}px` }}>
              <Button
                type="primary"
                onClick={() => {
                  window.location.href = teamId ? `/team/${teamId}/files` : '/';
                }}
              >
                Back to Files
              </Button>
            </div>
          </Card>
        </DetailsPageLayout.TopContent>
      );
    }

    if (isLoading) {
      return (
        <DetailsPageLayout.TopContent>
          <CenterSpin />
        </DetailsPageLayout.TopContent>
      );
    }

    if (error) {
      return (
        <DetailsPageLayout.TopContent>
          <Card>
            <Typography.Text type="danger">
              There was an error loading the file details. Please try again.
            </Typography.Text>
          </Card>
        </DetailsPageLayout.TopContent>
      );
    }

    if (!file) {
      return (
        <DetailsPageLayout.TopContent>
          <Card>
            <Typography.Text type="secondary">
              The file you're looking for could not be found.
            </Typography.Text>
          </Card>
        </DetailsPageLayout.TopContent>
      );
    }

    // Render success state with side content and main content
    return [
      <DetailsPageLayout.SideContent key="side-content" title="File Overview">
        <DetailRow>
          <Typography.Text>File ID</Typography.Text>
          <Tooltip title="Click to copy ID">
            <Typography.Text
              code
              style={{ cursor: 'pointer' }}
              onClick={handleCopyId}
            >
              {file.fileId}
            </Typography.Text>
          </Tooltip>
        </DetailRow>
        <DetailRow>
          <Typography.Text>Created</Typography.Text>
          <Typography.Text>
            {formatRelativeTime(file.createdAt)}
          </Typography.Text>
        </DetailRow>
        <DetailRow>
          <Typography.Text>Last Updated</Typography.Text>
          <Typography.Text>
            {formatRelativeTime(file.updatedAt)}
          </Typography.Text>
        </DetailRow>
        {file.uploadedBy && (
          <DetailRow>
            <Typography.Text>Uploaded By</Typography.Text>
            <Typography.Text>{file.uploadedBy}</Typography.Text>
          </DetailRow>
        )}
      </DetailsPageLayout.SideContent>,

      <DetailsPageLayout.MainContent
        key="main-content-information"
        title="File Information"
      >
        <SectionContainer>
          <UppercaseLabel type="secondary">BASIC INFORMATION</UppercaseLabel>
          <div style={{ marginTop: 8 }}>
            <DetailRow>
              <Text>File Name</Text>
              <Text strong>{file.fileName}</Text>
            </DetailRow>
            <DetailRow>
              <Text>File Size</Text>
              <Text>{formatBytes(file.fileSize)}</Text>
            </DetailRow>
            <DetailRow>
              <Text>File Type</Text>
              <Text code>{file.mimeType}</Text>
            </DetailRow>
            <DetailRow>
              <Text>Upload Status</Text>
              <div>{getUploadStatusTag(file.uploadStatus)}</div>
            </DetailRow>
          </div>
        </SectionContainer>

        {file.metadata && Object.keys(file.metadata).length > 0 && (
          <SectionContainer>
            <UppercaseLabel type="secondary">FILE METADATA</UppercaseLabel>
            <div style={{ marginTop: 8 }}>
              {Object.entries(file.metadata).map(([key, value]) => (
                <DetailRow key={key}>
                  <Text>{key}</Text>
                  <Text code>{String(value)}</Text>
                </DetailRow>
              ))}
            </div>
          </SectionContainer>
        )}
      </DetailsPageLayout.MainContent>,
      // do not need a file actions section yet
    ];
  };

  return (
    <div>
      <DetailsPageLayout {...layoutProps}>{renderContent()}</DetailsPageLayout>

      {file && file.uploadStatus === 'failed' && (
        <StatusBanner $status="failed">
          <Text strong>Upload failed</Text>
          <Text type="secondary">
            There was an error uploading this file. Please try uploading again.
          </Text>
        </StatusBanner>
      )}

      {file && file.uploadStatus === 'pending' && (
        <StatusBanner $status="pending">
          <Text strong>Upload in progress</Text>
          <Text type="secondary">
            This file is currently being uploaded and processed.
          </Text>
        </StatusBanner>
      )}
    </div>
  );
};
